import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { queryOptions, useQuery, UseQueryResult } from '@tanstack/react-query'
import { Address } from 'viem'
import { GLUEX_ROUTER_URL } from '@/config/consts'
import { BaseUnitNumber } from '@/domain/types/NumericValues'
import { toBigInt } from '@/utils/bigNumber'
import { GetPriceError, GetPriceResult, SwapError } from './types'
import { hypeAddressOverride } from './utils'
interface GetPriceParams {
  inputToken?: CheckedAddress
  outputToken?: CheckedAddress
  account?: Address
  inputAmount?: BaseUnitNumber
  enabled?: boolean
  resetErrors: () => void
}

const REFRESH_INTERVAL = 10000

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getPrice({ inputToken, outputToken, account, inputAmount, resetErrors }: GetPriceParams) {
  return queryOptions<GetPriceResult | GetPriceError | null>({
    queryKey: priceQueryKey({ inputToken, outputToken, account, inputAmount }),
    queryFn: async () => {
      if (!inputToken || !outputToken || !account || !inputAmount || inputToken === outputToken) {
        return null
      }

      resetErrors()

      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.NEXT_PUBLIC_GLUEX_API_KEY || '',
        },
        body: JSON.stringify({
          inputToken: hypeAddressOverride(inputToken),
          outputToken: hypeAddressOverride(outputToken),
          inputAmount: toBigInt(inputAmount).toString(),
          userAddress: account,
          outputReceiver: account,
          chainID: 'hyperevm',
          uniquePID: process.env.NEXT_PUBLIC_GLUEX_UUID,
          computeEstimates: true,
          computeStable: true,
        }),
      }

      try {
        const response = await fetch(`${GLUEX_ROUTER_URL}/v1/price`, fetchOptions)

        if (!response.ok) {
          throw new SwapError('Failed to generate quote', { response })
        }

        let returnData = await response.json()
        let retryCount = 0
        const maxRetries = 3

        while (returnData.statusCode.toString() !== '200' && retryCount < maxRetries) {
          retryCount++
          const retryResponse = await fetch(`${GLUEX_ROUTER_URL}/v1/price`, fetchOptions)

          if (!retryResponse.ok) {
            continue
          }
          returnData = await retryResponse.json()
        }

        if (returnData.error) {
          return returnData as GetPriceError
        }

        return returnData as GetPriceResult
      } catch (error) {
        console.error(error)
        return null
      }
    },
    enabled: Boolean(inputToken && outputToken && account && inputAmount && inputAmount.gt(0)),
    refetchInterval: REFRESH_INTERVAL,
    gcTime: 0,
  })
}

type PriceQueryKeyParams = Omit<GetPriceParams, 'resetErrors'>

export function priceQueryKey({ inputToken, outputToken, account, inputAmount }: PriceQueryKeyParams): unknown[] {
  return [...getPriceQueryKeyPrefix({ inputToken, outputToken, account, inputAmount })]
}

function getPriceQueryKeyPrefix({ inputToken, outputToken, account, inputAmount }: PriceQueryKeyParams): unknown[] {
  return ['price', inputToken, outputToken, account, inputAmount]
}

export type UseGetPriceResult = UseQueryResult & { price?: GetPriceResult | GetPriceError | null }

export function useGetPrice({
  inputToken,
  outputToken,
  account,
  inputAmount,
  enabled,
  resetErrors,
}: GetPriceParams): UseGetPriceResult {
  const response = useQuery({
    ...getPrice({
      inputToken,
      outputToken,
      account,
      inputAmount,
      resetErrors,
    }),
    enabled,
  })

  return {
    ...response,
    price: response.data,
  }
}
